/**
 * Zod Schemas for AI Suggest API
 *
 * Provides runtime validation schemas for AI Button suggestion endpoints:
 * - POST /ai/suggest
 * - POST /ai/feedback
 * - POST /ai/apply
 *
 * @module schemas/ai-suggest.zod
 * @see SPEC-012 Requirements 1.1, 8.1, 9.1
 */
import { z } from 'zod';
import { HARD_CAP_PER_KIND, MAX_REASON_LENGTH } from '../types/ai-suggest';
// ============================================================================
// Shared Schemas
// ============================================================================
/**
 * Query ID schema - identifies query strategy.
 *
 * @see Requirement 2.1
 */
export const QueryIdSchema = z.enum(['overview', 'recent']);
/**
 * Candidate source schema.
 *
 * @see Requirement 5.1
 */
export const CandidateSourceSchema = z.enum(['vector', 'graph', 'recency']);
/**
 * Candidate kind schema.
 *
 * @see Requirement 8.2
 */
export const CandidateKindSchema = z.enum(['backlink', 'tag']);
/**
 * Score breakdown schema - normalized scores (0-1 range).
 *
 * @see Requirement 6.1
 */
export const ScoreBreakdownSchema = z.object({
    vec01: z.number().min(0).max(1),
    graph01: z.number().min(0).max(1),
    time01: z.number().min(0).max(1),
    feedback01: z.number().min(0).max(1),
});
/**
 * Degrade reason schema.
 *
 * - `vector_down`: Vector service is temporarily unavailable
 * - `graph_empty`: No graph edges available for the document
 * - `quota`: User has exceeded their quota
 * - `other`: Other unspecified reason
 * - `vector_not_configured`: Vector service is not configured (SPEC-013)
 * - `vector_connection_failed`: Vector service connection failed (SPEC-013)
 * - `insufficient_docs`: Not enough documents for meaningful suggestions (SPEC-013)
 *
 * @see Requirement 11.1
 * @see SPEC-013 P1 Scenario 1, 2
 */
export const DegradeReasonSchema = z.enum([
    'vector_down',
    'graph_empty',
    'quota',
    'other',
    'vector_not_configured',
    'vector_connection_failed',
    'insufficient_docs',
]);
/**
 * Feedback action schema.
 *
 * @see Requirement 8.3
 */
export const FeedbackActionSchema = z.enum(['up', 'down']);
/**
 * Apply selection schema - candidates to apply/reject.
 *
 * @see Requirement 9.1
 */
export const ApplySelectionSchema = z.object({
    backlinks: z.array(z.string()).max(HARD_CAP_PER_KIND),
    tags: z.array(z.string()).max(HARD_CAP_PER_KIND),
});
/**
 * Suggest hint schema - provides i18n-ready hints for UI display.
 *
 * Used when suggestions are degraded to provide actionable guidance to users.
 *
 * @see SPEC-013 P2 Scenario 1, 2
 */
export const SuggestHintSchema = z.object({
    /** i18n key for the hint message */
    key: z.string().min(1, 'hint key is required'),
    /** Optional interpolation parameters for the i18n message */
    params: z.record(z.unknown()).optional(),
});
// ============================================================================
// Request Schemas
// ============================================================================
/**
 * Schema for POST /ai/suggest request.
 *
 * @see Requirement 1.1
 */
export const SuggestRequestSchema = z.object({
    docId: z.string().min(1, 'docId is required'),
});
/**
 * Schema for POST /ai/feedback request.
 *
 * @see Requirement 8.1
 */
export const FeedbackRequestSchema = z.object({
    docId: z.string().min(1, 'docId is required'),
    proposalId: z.string().min(1, 'proposalId is required'),
    candidateId: z.string().min(1, 'candidateId is required'),
    kind: CandidateKindSchema,
    action: FeedbackActionSchema,
});
/**
 * Schema for POST /ai/apply request.
 *
 * @see Requirement 9.1
 */
export const ApplyRequestSchema = z.object({
    docId: z.string().min(1, 'docId is required'),
    proposalId: z.string().min(1, 'proposalId is required'),
    accepted: ApplySelectionSchema,
    rejected: ApplySelectionSchema,
});
// ============================================================================
// Response Schemas
// ============================================================================
/**
 * Schema for backlink suggestion in response.
 *
 * @see Requirement 1.3
 */
export const BacklinkSuggestionSchema = z.object({
    docId: z.string().min(1),
    title: z.string(),
    reason: z.string().max(MAX_REASON_LENGTH),
    score: z.number(),
    scoreBreakdown: ScoreBreakdownSchema,
});
/**
 * Schema for tag suggestion in response.
 *
 * @see Requirement 1.3
 */
export const TagSuggestionSchema = z.object({
    tag: z.string().min(1),
    reason: z.string().max(MAX_REASON_LENGTH),
    score: z.number(),
    scoreBreakdown: ScoreBreakdownSchema,
});
/**
 * Schema for POST /ai/suggest response.
 *
 * @see Requirement 1.2
 * @see SPEC-013 P2 - hint field for degraded responses
 */
export const SuggestResponseSchema = z.object({
    proposalId: z.string().min(1),
    backlinks: z.array(BacklinkSuggestionSchema).max(HARD_CAP_PER_KIND),
    tags: z.array(TagSuggestionSchema).max(HARD_CAP_PER_KIND),
    degraded: z.boolean(),
    degradeReason: DegradeReasonSchema.optional(),
    /** Optional hint for UI display when degraded */
    hint: SuggestHintSchema.optional(),
});
/**
 * Schema for POST /ai/feedback response.
 *
 * @see Requirement 8.4
 */
export const FeedbackResponseSchema = z.object({
    success: z.boolean(),
    recordedAt: z.number().nonnegative(),
});
/**
 * Schema for POST /ai/apply response.
 *
 * @see Requirement 9.5
 */
export const ApplyResponseSchema = z.object({
    success: z.boolean(),
    appliedAt: z.number().nonnegative(),
    warnings: z.array(z.string()).optional(),
});
//# sourceMappingURL=ai-suggest.zod.js.map